import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';

// ── Types ─────────────────────────────────────────────────────────

interface OrgConfig {
  org_id: string;
  org_name: string;
  selected_metrics: string[];
  output_format: string;
  reporting_period: 'monthly' | 'quarterly';
  manual_overrides: Record<string, number>;
  threshold_overrides: Record<string, number>;
  format_id: string;
  schedule_enabled: boolean;
  schedule_type: string;
  schedule_day: number;
  send_enabled: boolean;
  contacts: Contact[];
  created_at?: string;
  updated_at?: string;
}

interface Contact {
  contact_id?: string;
  name: string;
  email: string;
  type: 'primary' | 'cc';
  is_default: boolean;
}

interface MetricDef {
  key: string;
  category: string;
  display_order: number;
  label: string;
  definition: string;
  compute: 'sql' | 'manual' | 'derived' | 'na';
  value_format: string;
  field_status: 'confirmed' | 'needs_validation';
  depends_on?: string[];
}

interface QueueReport {
  report_id: string;
  org_id: string;
  org_name: string;
  format_id: string;
  period_start: string;
  period_end: string;
  status: 'pending_review' | 'approved' | 'sent';
  triage: 'ready' | 'needs_intervention';
  s3_key: string;
  intervention_items: InterventionItem[];
  generated_at: string;
  trigger_type: string;
  approved_by?: string;
  approved_at?: string;
  sent_at?: string;
  recipients: any[];
}

interface InterventionItem {
  metric_key: string;
  metric_label: string;
  reason: string;
  message: string;
}

interface MetricResult {
  key: string;
  category: string;
  label: string;
  value: any;
  definition: string;
  field_status: string;
  display_order: number;
}

type View = 'queue' | 'orgs' | 'report-detail' | 'org-editor' | 'generate';

// ── Badges ────────────────────────────────────────────────────────

function TriageBadge({ triage, count }: { triage: string; count?: number }) {
  const cls = triage === 'ready'
    ? 'bg-green-100 text-green-700'
    : 'bg-amber-100 text-amber-700';
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {triage === 'ready' ? 'Ready' : `Needs Intervention${count ? ` (${count})` : ''}`}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    pending_review: 'bg-slate-100 text-slate-600',
    approved: 'bg-blue-100 text-blue-700',
    sent: 'bg-gray-200 text-gray-600',
  };
  const labels: Record<string, string> = {
    pending_review: 'Pending Review',
    approved: 'Approved',
    sent: 'Sent',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-slate-100 text-slate-600'}`}>
      {labels[status] || status}
    </span>
  );
}

function FormatBadge({ formatId }: { formatId: string }) {
  return formatId === 'format_b'
    ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">Format B (Annual)</span>
    : <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">Format A</span>;
}

// ── Main Component ────────────────────────────────────────────────

export default function SlaReporting() {
  const [view, setView] = useState<View>('queue');
  const [orgs, setOrgs] = useState<OrgConfig[]>([]);
  const [metrics, setMetrics] = useState<MetricDef[]>([]);
  const [queue, setQueue] = useState<QueueReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<QueueReport | null>(null);
  const [editingOrg, setEditingOrg] = useState<OrgConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [queueFilter, setQueueFilter] = useState<string>('all');

  // Generate form state
  const [generateOrgId, setGenerateOrgId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [generating, setGenerating] = useState(false);

  const fetchOrgs = useCallback(async () => {
    try {
      const data = await api.getSlaOrgs();
      setOrgs(data.orgs || []);
    } catch (err: any) { setError(err.message); }
  }, []);

  const fetchMetrics = useCallback(async () => {
    try {
      const data = await api.getSlaMetrics();
      setMetrics(data.metrics || []);
    } catch (err: any) { setError(err.message); }
  }, []);

  const fetchQueue = useCallback(async () => {
    try {
      const params: any = {};
      if (queueFilter === 'ready') params.triage = 'ready';
      else if (queueFilter === 'needs_intervention') params.triage = 'needs_intervention';
      else if (queueFilter === 'approved') params.status = 'approved';
      else if (queueFilter === 'sent') params.status = 'sent';
      const data = await api.getSlaQueue(params);
      setQueue(data.reports || []);
    } catch (err: any) { setError(err.message); }
  }, [queueFilter]);

  useEffect(() => { fetchOrgs(); fetchMetrics(); }, [fetchOrgs, fetchMetrics]);
  useEffect(() => { if (view === 'queue') fetchQueue(); }, [view, fetchQueue]);

  const handleGenerateAllDue = async () => {
    setLoading(true);
    setError('');
    try {
      await api.generateAllDue();
      await fetchQueue();
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleGenerate = async () => {
    if (!generateOrgId || !startDate || !endDate) {
      setError('Org, start date, and end date required');
      return;
    }
    setGenerating(true);
    setError('');
    try {
      const result = await api.generateSlaReport({
        org_id: generateOrgId,
        start_date: startDate,
        end_date: endDate,
        report_date: endDate,
        trigger_type: 'manual',
      });
      if (result.download_url) {
        const a = document.createElement('a');
        a.href = result.download_url;
        a.download = result.filename || 'SLA_Report.xlsx';
        a.click();
      }
      await fetchQueue();
      setView('queue');
    } catch (err: any) { setError(err.message); }
    finally { setGenerating(false); }
  };

  const handleDeleteReport = async (reportId: string) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await api.deleteSlaReport(reportId);
      await fetchQueue();
      if (selectedReport?.report_id === reportId) {
        setSelectedReport(null);
        setView('queue');
      }
    } catch (err: any) { setError(err.message); }
  };

  const handleDownload = async (reportId: string) => {
    try {
      const data = await api.getSlaQueueDownload(reportId);
      if (data.download_url) {
        const a = document.createElement('a');
        a.href = data.download_url;
        a.download = `SLA_Report_${reportId}.xlsx`;
        a.click();
      }
    } catch (err: any) { setError(err.message); }
  };

  const openReport = async (report: QueueReport) => {
    setSelectedReport(report);
    setView('report-detail');
  };

  const handleDeleteOrg = async (orgId: string) => {
    if (!window.confirm(`Delete config for ${orgId}?`)) return;
    try {
      await api.deleteSlaOrg(orgId);
      await fetchOrgs();
    } catch (err: any) { setError(err.message); }
  };

  const handleSaveOrg = async (config: OrgConfig) => {
    setLoading(true);
    setError('');
    try {
      if (editingOrg?.org_id) {
        await api.updateSlaOrg(config.org_id, config);
      } else {
        await api.createSlaOrg(config);
      }
      await fetchOrgs();
      setView('orgs');
      setEditingOrg(null);
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  };

  const startEdit = (org?: OrgConfig) => {
    setEditingOrg(org || {
      org_id: '', org_name: '', selected_metrics: [],
      output_format: '3col', reporting_period: 'monthly',
      manual_overrides: {}, threshold_overrides: {},
      format_id: 'format_a', schedule_enabled: false,
      schedule_type: 'monthly', schedule_day: 1,
      send_enabled: false, contacts: [],
    });
    setView('org-editor');
  };

  // Group metrics by category
  const metricsByCategory: Record<string, MetricDef[]> = {};
  metrics.forEach(m => {
    if (!metricsByCategory[m.category]) metricsByCategory[m.category] = [];
    metricsByCategory[m.category].push(m);
  });

  const filteredQueue = queue.filter(r => {
    if (queueFilter === 'all') return true;
    if (queueFilter === 'ready') return r.triage === 'ready';
    if (queueFilter === 'needs_intervention') return r.triage === 'needs_intervention';
    if (queueFilter === 'approved') return r.status === 'approved';
    if (queueFilter === 'sent') return r.status === 'sent';
    return true;
  });

  const countFor = (filter: string) => queue.filter(r => {
    if (filter === 'ready') return r.triage === 'ready';
    if (filter === 'needs_intervention') return r.triage === 'needs_intervention';
    if (filter === 'approved') return r.status === 'approved';
    if (filter === 'sent') return r.status === 'sent';
    return true;
  }).length;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">SLA Reports</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setView('queue')}
            className={`px-3 py-1.5 text-sm rounded ${view === 'queue' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            Report Queue
          </button>
          <button
            onClick={() => setView('orgs')}
            className={`px-3 py-1.5 text-sm rounded ${view === 'orgs' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            Organizations
          </button>
          <button
            onClick={() => setView('generate')}
            className="px-3 py-1.5 text-sm rounded bg-green-600 text-white hover:bg-green-700"
          >
            + Generate Report
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 ml-2">x</button>
        </div>
      )}

      {/* ── Report Queue View ── */}
      {view === 'queue' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-1">
              {(['all', 'ready', 'needs_intervention', 'approved', 'sent'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setQueueFilter(f)}
                  className={`px-3 py-1.5 text-xs rounded-full font-medium ${
                    queueFilter === f ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {f === 'all' ? 'All' : f === 'ready' ? 'Ready' : f === 'needs_intervention' ? 'Needs Intervention' : f === 'approved' ? 'Approved' : 'Sent'}
                  <span className="ml-1 opacity-70">{countFor(f)}</span>
                </button>
              ))}
            </div>
            <button
              onClick={handleGenerateAllDue}
              disabled={loading}
              className="px-3 py-1.5 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
            >
              {loading ? 'Generating...' : 'Generate All Due Now'}
            </button>
          </div>

          {filteredQueue.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-slate-200 p-12 text-center">
              <p className="text-slate-500 text-sm">No reports in queue.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredQueue.map(report => (
                <div
                  key={report.report_id}
                  className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 hover:border-slate-300 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-slate-800">{report.org_name}</span>
                          <FormatBadge formatId={report.format_id} />
                          <StatusBadge status={report.status} />
                          <TriageBadge triage={report.triage} count={report.intervention_items?.length} />
                        </div>
                        <div className="text-xs text-slate-500 flex gap-3">
                          <span>{report.period_start} to {report.period_end}</span>
                          <span>Generated {new Date(report.generated_at).toLocaleDateString()}</span>
                          <span className="capitalize">{report.trigger_type}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openReport(report)}
                        className="px-3 py-1 text-xs rounded bg-slate-100 text-slate-600 hover:bg-slate-200"
                      >
                        Preview
                      </button>
                      <button
                        onClick={() => handleDownload(report.report_id)}
                        className="px-3 py-1 text-xs rounded bg-green-50 text-green-700 hover:bg-green-100"
                      >
                        Download
                      </button>
                      <button
                        onClick={() => handleDeleteReport(report.report_id)}
                        className="px-3 py-1 text-xs rounded bg-red-50 text-red-500 hover:bg-red-100"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Report Detail View ── */}
      {view === 'report-detail' && selectedReport && (
        <ReportDetail
          report={selectedReport}
          onBack={() => { setView('queue'); fetchQueue(); }}
          onError={setError}
        />
      )}

      {/* ── Organizations View ── */}
      {view === 'orgs' && (
        <div>
          <div className="flex justify-end mb-4">
            <button
              onClick={() => startEdit()}
              className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              + New Org
            </button>
          </div>
          {orgs.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-slate-200 p-12 text-center">
              <p className="text-slate-500 text-sm">No organizations configured.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">Organization</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">ID</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">Format</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">Schedule</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">Metrics</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map(org => (
                    <tr key={org.org_id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-2 font-medium">{org.org_name}</td>
                      <td className="px-4 py-2 text-slate-500 font-mono text-xs">{org.org_id}</td>
                      <td className="px-4 py-2"><FormatBadge formatId={org.format_id || 'format_a'} /></td>
                      <td className="px-4 py-2">
                        {org.schedule_enabled ? (
                          <span className="text-green-600 text-xs font-medium capitalize">{org.schedule_type || org.reporting_period}</span>
                        ) : (
                          <span className="text-slate-400 text-xs">Disabled</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500">{org.selected_metrics?.length || 0} selected</td>
                      <td className="px-4 py-2 text-right space-x-2">
                        <button onClick={() => { setGenerateOrgId(org.org_id); setView('generate'); }}
                          className="text-green-600 hover:text-green-800 text-xs font-medium">Generate</button>
                        <button onClick={() => startEdit(org)}
                          className="text-blue-600 hover:text-blue-800 text-xs">Edit</button>
                        <button onClick={() => handleDeleteOrg(org.org_id)}
                          className="text-red-400 hover:text-red-600 text-xs">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Org Editor View ── */}
      {view === 'org-editor' && editingOrg && (
        <OrgEditor
          org={editingOrg}
          metricsByCategory={metricsByCategory}
          onSave={handleSaveOrg}
          onCancel={() => { setView('orgs'); setEditingOrg(null); }}
          loading={loading}
        />
      )}

      {/* ── Generate Report View ── */}
      {view === 'generate' && (
        <div className="bg-white rounded-lg shadow border border-slate-200 p-6 max-w-xl">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Generate SLA Report</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Organization</label>
              <select value={generateOrgId} onChange={e => setGenerateOrgId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
                <option value="">Select org...</option>
                {orgs.map(o => (
                  <option key={o.org_id} value={o.org_id}>{o.org_name} ({o.org_id})</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={handleGenerate}
                disabled={generating || !generateOrgId || !startDate || !endDate}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-40 text-sm">
                {generating ? 'Generating...' : 'Generate & Download'}
              </button>
              <button onClick={() => setView('queue')}
                className="px-4 py-2 bg-slate-100 text-slate-600 rounded hover:bg-slate-200 text-sm">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Report Detail Component ───────────────────────────────────────

function ReportDetail({
  report,
  onBack,
  onError,
}: {
  report: QueueReport;
  onBack: () => void;
  onError: (msg: string) => void;
}) {
  const [preview, setPreview] = useState<{ metrics: MetricResult[]; triage: string; intervention_items: InterventionItem[] } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [approving, setApproving] = useState(false);
  const [sending, setSending] = useState(false);
  const [manualOverrides, setManualOverrides] = useState<Record<string, string>>({});
  const [sendResult, setSendResult] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = await api.getSlaQueuePreview(report.report_id);
        setPreview(data);
      } catch (err: any) { onError(err.message); }
      finally { setLoadingPreview(false); }
    })();
  }, [report.report_id, onError]);

  const handleApprove = async (andSend: boolean) => {
    setApproving(true);
    try {
      const overrides: Record<string, number> = {};
      Object.entries(manualOverrides).forEach(([k, v]) => {
        if (v !== '') overrides[k] = Number(v);
      });

      await api.approveSlaReport(report.report_id, {
        approved_by: 'portal_user',
        manual_overrides: Object.keys(overrides).length > 0 ? overrides : undefined,
      });

      if (andSend) {
        setSending(true);
        const result = await api.sendSlaReport(report.report_id);
        setSendResult(result.message || result.status || 'Sent');
        setSending(false);
      }
      onBack();
    } catch (err: any) { onError(err.message); }
    finally { setApproving(false); }
  };

  const handleDownload = async () => {
    try {
      const data = await api.getSlaQueueDownload(report.report_id);
      if (data.download_url) {
        const a = document.createElement('a');
        a.href = data.download_url;
        a.download = `SLA_Report_${report.report_id}.xlsx`;
        a.click();
      }
    } catch (err: any) { onError(err.message); }
  };

  // Group metrics by category for display
  const metricsByCategory: Record<string, MetricResult[]> = {};
  preview?.metrics?.forEach(m => {
    if (!metricsByCategory[m.category]) metricsByCategory[m.category] = [];
    metricsByCategory[m.category].push(m);
  });

  const interventionKeys = new Set((report.intervention_items || []).map(i => i.metric_key));

  return (
    <div>
      <button onClick={onBack} className="mb-4 text-sm text-slate-500 hover:text-slate-700">
        &larr; Back to Queue
      </button>

      <div className="grid grid-cols-3 gap-6">
        {/* Left Panel: Preview */}
        <div className="col-span-2 bg-white rounded-lg shadow border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-800">Report Preview</h2>
            <div className="flex gap-2">
              <FormatBadge formatId={report.format_id} />
              <StatusBadge status={report.status} />
              <TriageBadge triage={report.triage} count={report.intervention_items?.length} />
            </div>
          </div>

          <div className="text-sm text-slate-500 mb-4">
            <strong>{report.org_name}</strong> &mdash; {report.period_start} to {report.period_end}
          </div>

          {loadingPreview ? (
            <div className="text-slate-400 text-sm py-8 text-center">Loading preview...</div>
          ) : report.format_id === 'format_b' ? (
            <div className="bg-purple-50 border border-purple-200 rounded p-4">
              <p className="text-sm text-purple-800">
                <strong>Format B Annual Workbook</strong> &mdash; Monthly row for {report.period_start?.slice(0, 7)} populated.
              </p>
              <p className="text-xs text-purple-600 mt-1">
                {preview?.metrics?.filter(m => m.value !== 'N/A' && m.value !== null).length || 0} of {preview?.metrics?.length || 0} metric cells filled.
                {' '}{preview?.intervention_items?.length || 0} cells need manual entry.
              </p>
              <button onClick={handleDownload}
                className="mt-3 px-3 py-1 text-xs rounded bg-purple-600 text-white hover:bg-purple-700">
                Download Annual Workbook
              </button>
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 text-slate-600 font-medium">Metric</th>
                    <th className="text-right py-2 px-2 text-slate-600 font-medium w-28">Value</th>
                    <th className="text-left py-2 px-2 text-slate-600 font-medium">Definition</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(metricsByCategory).map(([category, categoryMetrics]) => (
                    <>
                      <tr key={`cat-${category}`}>
                        <td colSpan={3} className="pt-3 pb-1 px-2 font-bold text-slate-800 text-xs uppercase tracking-wide bg-slate-50">
                          {category}
                        </td>
                      </tr>
                      {categoryMetrics.map(m => {
                        const isIntervention = interventionKeys.has(m.key);
                        return (
                          <tr key={m.key} className={`border-b ${isIntervention ? 'bg-red-50' : 'hover:bg-slate-50'}`}>
                            <td className="py-1.5 px-2">
                              {m.label}
                              {isIntervention && <span className="ml-1 text-red-500 text-xs">*</span>}
                            </td>
                            <td className={`py-1.5 px-2 text-right font-mono ${m.value === 'N/A' ? 'text-slate-400' : ''}`}>
                              {m.value ?? 'N/A'}
                            </td>
                            <td className="py-1.5 px-2 text-xs text-slate-500">{m.definition}</td>
                          </tr>
                        );
                      })}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right Panel: Actions */}
        <div className="space-y-4">
          {/* Intervention Items */}
          {report.intervention_items?.length > 0 && (
            <div className="bg-white rounded-lg shadow border border-amber-200 p-4">
              <h3 className="text-sm font-semibold text-amber-800 mb-3">
                Needs Intervention ({report.intervention_items.length})
              </h3>
              <div className="space-y-2">
                {report.intervention_items.map(item => (
                  <div key={item.metric_key} className="text-xs border-b border-amber-100 pb-2">
                    <div className="font-medium text-slate-700">{item.metric_label}</div>
                    <div className="text-slate-500">{item.message}</div>
                    {item.reason === 'manual_required' && (
                      <input
                        type="number"
                        placeholder="Enter value"
                        value={manualOverrides[item.metric_key] || ''}
                        onChange={e => setManualOverrides(prev => ({ ...prev, [item.metric_key]: e.target.value }))}
                        className="mt-1 w-full px-2 py-1 border border-slate-300 rounded text-xs"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="bg-white rounded-lg shadow border border-slate-200 p-4 space-y-3">
            <button onClick={handleDownload}
              className="w-full px-3 py-2 text-sm rounded bg-green-50 text-green-700 hover:bg-green-100 border border-green-200">
              Download Excel
            </button>
            <button onClick={() => handleApprove(false)}
              disabled={approving}
              className="w-full px-3 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">
              {approving ? 'Approving...' : 'Approve Only'}
            </button>
            <button onClick={() => handleApprove(true)}
              disabled={approving || sending}
              className="w-full px-3 py-2 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
              {sending ? 'Sending...' : 'Approve & Send'}
            </button>
            {sendResult && (
              <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded">{sendResult}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Org Editor Component ──────────────────────────────────────────

function OrgEditor({
  org,
  metricsByCategory,
  onSave,
  onCancel,
  loading,
}: {
  org: OrgConfig;
  metricsByCategory: Record<string, MetricDef[]>;
  onSave: (config: OrgConfig) => void;
  onCancel: () => void;
  loading: boolean;
}) {
  const [formData, setFormData] = useState<OrgConfig>({ ...org });
  const [tab, setTab] = useState<'settings' | 'metrics' | 'contacts'>('settings');
  const isNew = !org.org_id;

  const toggleMetric = (key: string) => {
    setFormData(prev => ({
      ...prev,
      selected_metrics: prev.selected_metrics.includes(key)
        ? prev.selected_metrics.filter(k => k !== key)
        : [...prev.selected_metrics, key],
    }));
  };

  const setManualOverride = (key: string, val: string) => {
    const overrides = { ...formData.manual_overrides };
    if (val === '') delete overrides[key];
    else overrides[key] = Number(val);
    setFormData(prev => ({ ...prev, manual_overrides: overrides }));
  };

  const addContact = () => {
    setFormData(prev => ({
      ...prev,
      contacts: [...(prev.contacts || []), { name: '', email: '', type: 'primary', is_default: true }],
    }));
  };

  const updateContact = (index: number, field: string, value: any) => {
    setFormData(prev => {
      const contacts = [...(prev.contacts || [])];
      contacts[index] = { ...contacts[index], [field]: value };
      return { ...prev, contacts };
    });
  };

  const removeContact = (index: number) => {
    setFormData(prev => ({
      ...prev,
      contacts: (prev.contacts || []).filter((_, i) => i !== index),
    }));
  };

  const selectedManualMetrics = Object.values(metricsByCategory)
    .flat()
    .filter(m => m.compute === 'manual' && formData.selected_metrics.includes(m.key));

  return (
    <div className="bg-white rounded-lg shadow border border-slate-200 p-6">
      <h2 className="text-lg font-semibold text-slate-800 mb-4">
        {isNew ? 'New Organization Config' : `Edit: ${org.org_name}`}
      </h2>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-6">
        {(['settings', 'metrics', 'contacts'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>
            {t === 'settings' ? 'Settings' : t === 'metrics' ? 'Metrics' : 'Contacts'}
          </button>
        ))}
      </div>

      {/* Settings Tab */}
      {tab === 'settings' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Organization ID</label>
              <input type="text" value={formData.org_id}
                onChange={e => setFormData(prev => ({ ...prev, org_id: e.target.value }))}
                disabled={!isNew} placeholder="e.g. C05661"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm disabled:bg-slate-100" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Organization Name</label>
              <input type="text" value={formData.org_name}
                onChange={e => setFormData(prev => ({ ...prev, org_name: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Report Format</label>
            <div className="grid grid-cols-2 gap-3">
              <label className={`flex items-center gap-3 p-3 border rounded cursor-pointer ${
                formData.format_id === 'format_a' ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'
              }`}>
                <input type="radio" checked={formData.format_id === 'format_a'}
                  onChange={() => setFormData(prev => ({ ...prev, format_id: 'format_a', output_format: '3col' }))} />
                <div>
                  <div className="text-sm font-medium">Format A — Vertical Snapshot</div>
                  <div className="text-xs text-slate-500">Standard metric table (2 or 3 column)</div>
                </div>
              </label>
              <label className={`flex items-center gap-3 p-3 border rounded cursor-pointer ${
                formData.format_id === 'format_b' ? 'border-purple-400 bg-purple-50' : 'border-slate-200 hover:bg-slate-50'
              }`}>
                <input type="radio" checked={formData.format_id === 'format_b'}
                  onChange={() => setFormData(prev => ({ ...prev, format_id: 'format_b', output_format: 'format_b' }))} />
                <div>
                  <div className="text-sm font-medium">Format B — Annual Workbook</div>
                  <div className="text-xs text-slate-500">Lloyd's DCA style, monthly rows</div>
                </div>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Schedule</label>
              <label className="flex items-center gap-2 mt-1">
                <input type="checkbox" checked={formData.schedule_enabled}
                  onChange={e => setFormData(prev => ({ ...prev, schedule_enabled: e.target.checked }))} />
                <span className="text-sm text-slate-600">Auto-generate</span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Frequency</label>
              <select value={formData.schedule_type}
                onChange={e => setFormData(prev => ({ ...prev, schedule_type: e.target.value, reporting_period: e.target.value as any }))}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Send Enabled</label>
              <label className="flex items-center gap-2 mt-1">
                <input type="checkbox" checked={formData.send_enabled} disabled
                  onChange={e => setFormData(prev => ({ ...prev, send_enabled: e.target.checked }))} />
                <span className="text-sm text-slate-400">Disabled globally</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Metrics Tab */}
      {tab === 'metrics' && (
        <div className="space-y-4">
          {Object.entries(metricsByCategory).map(([category, categoryMetrics]) => (
            <div key={category}>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{category}</h4>
              <div className="space-y-1 ml-2">
                {categoryMetrics.map(m => (
                  <label key={m.key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-2 py-1 rounded">
                    <input type="checkbox" checked={formData.selected_metrics.includes(m.key)}
                      onChange={() => toggleMetric(m.key)} />
                    <span className="flex-1">{m.label}</span>
                    {m.field_status === 'needs_validation' && (
                      <span className="text-amber-500 text-xs">[pending]</span>
                    )}
                    {m.compute === 'manual' && <span className="text-blue-500 text-xs">[manual]</span>}
                  </label>
                ))}
              </div>
            </div>
          ))}

          {selectedManualMetrics.length > 0 && (
            <div className="pt-4 border-t">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Manual Values</h3>
              <div className="space-y-2 ml-2">
                {selectedManualMetrics.map(m => (
                  <div key={m.key} className="flex items-center gap-3">
                    <label className="text-sm text-slate-600 flex-1">{m.label}</label>
                    <input type="number" value={formData.manual_overrides?.[m.key] ?? ''}
                      onChange={e => setManualOverride(m.key, e.target.value)}
                      className="w-24 px-2 py-1 border border-slate-300 rounded text-sm text-right" placeholder="0" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contacts Tab */}
      {tab === 'contacts' && (
        <div className="space-y-3">
          {(formData.contacts || []).length === 0 ? (
            <p className="text-sm text-slate-400">No contacts configured.</p>
          ) : (
            (formData.contacts || []).map((contact, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                <input type="text" value={contact.name} onChange={e => updateContact(i, 'name', e.target.value)}
                  placeholder="Name" className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm" />
                <input type="email" value={contact.email} onChange={e => updateContact(i, 'email', e.target.value)}
                  placeholder="Email" className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm" />
                <select value={contact.type} onChange={e => updateContact(i, 'type', e.target.value)}
                  className="px-2 py-1 border border-slate-300 rounded text-sm">
                  <option value="primary">Primary</option>
                  <option value="cc">CC</option>
                </select>
                <label className="flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={contact.is_default}
                    onChange={e => updateContact(i, 'is_default', e.target.checked)} />
                  Default
                </label>
                <button onClick={() => removeContact(i)} className="text-red-400 hover:text-red-600 text-xs">Remove</button>
              </div>
            ))
          )}
          <button onClick={addContact}
            className="px-3 py-1 text-xs rounded bg-slate-100 text-slate-600 hover:bg-slate-200">
            + Add Contact
          </button>
        </div>
      )}

      {/* Save / Cancel */}
      <div className="flex gap-3 pt-6 mt-6 border-t">
        <button onClick={() => onSave(formData)}
          disabled={loading || !formData.org_id || !formData.org_name}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-40">
          {loading ? 'Saving...' : 'Save Config'}
        </button>
        <button onClick={onCancel}
          className="px-4 py-2 bg-slate-100 text-slate-600 text-sm rounded hover:bg-slate-200">
          Cancel
        </button>
      </div>
    </div>
  );
}
